import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";
import { z } from "zod";

const STORAGE_KEY = "pdn_exit_intent_shown_v1";
const COOLDOWN_DAYS = 14;

const emailSchema = z.string().email().max(150);

const shouldShow = (): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return true;
    const t = parseInt(raw, 10);
    if (!t) return true;
    return Date.now() - t > COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch { return true; }
};

const markShown = () => {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
};

const ExitIntentPopup = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [armed, setArmed] = useState(false);

  // Skip on admin pages
  const isExcluded = location.pathname.startsWith("/admin") || location.pathname.startsWith("/maintenance");

  // Arm after a short delay so it doesn't fire on initial cursor movement
  useEffect(() => {
    if (isExcluded || !shouldShow()) return;
    const t = window.setTimeout(() => setArmed(true), 8000);
    return () => window.clearTimeout(t);
  }, [isExcluded]);

  // Desktop: mouse leaves toward top
  useEffect(() => {
    if (!armed) return;
    const onLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && e.relatedTarget === null) {
        setOpen(true);
        markShown();
        setArmed(false);
      }
    };
    document.addEventListener("mouseout", onLeave);
    return () => document.removeEventListener("mouseout", onLeave);
  }, [armed]);

  // Mobile: trigger after sustained inactivity + scroll-up
  useEffect(() => {
    if (!armed) return;
    if (window.matchMedia("(min-width: 768px)").matches) return;

    let lastY = window.scrollY;
    let lastInteraction = Date.now();

    const onScroll = () => {
      const y = window.scrollY;
      const goingUp = y < lastY - 40;
      const idleMs = Date.now() - lastInteraction;
      if (goingUp && y < 200 && idleMs > 15000) {
        setOpen(true);
        markShown();
        setArmed(false);
      }
      lastY = y;
      lastInteraction = Date.now();
    };
    const onTouch = () => { lastInteraction = Date.now(); };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("touchstart", onTouch, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("touchstart", onTouch);
    };
  }, [armed]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = emailSchema.safeParse(email.trim());
    if (!result.success) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await subscribeNewsletter(result.data, "exit_intent");
      toast.success("You're in! Watch your inbox for new premium drops.");
      setOpen(false);
      setEmail("");
    } catch (err: any) {
      toast.error(err?.message || "Subscription failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (isExcluded) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <div className="mx-auto -mt-2 mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
          <Sparkles className="h-6 w-6 text-primary" />
        </div>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-center">
            Wait — don't miss our next drop
          </DialogTitle>
          <DialogDescription className="text-center">
            Get first access to newly listed premium domains and private deals — straight to your inbox.
            No spam, unsubscribe anytime.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="email"
              placeholder="you@example.com"
              className="pl-9 h-11"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <Button type="submit" className="w-full h-11 font-semibold" disabled={submitting}>
            {submitting ? "Subscribing..." : "Get early access"}
          </Button>
          <p className="text-[11px] text-center text-muted-foreground">
            Join investors and founders already on our list.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentPopup;
